import { MfaPolicy } from '@piccolohealth/pbs-common';
import { ChakraV3, Spin } from '@piccolohealth/ui';
import { P, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormSection } from '../../components/forms/FormSection';
import { FormStack } from '../../components/forms/FormStack';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedSegmentGroup } from '../../components/forms/HookedSegmentGroup';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useUpdateOrganizationMutation } from '../../graphql/hooks/useUpdateOrganizationMutation';
import { useUsersQuery } from '../../graphql/hooks/useUsersQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { zodResolver } from '../../utils/zod';

const mfaPolicyOptions = [
  {
    key: MfaPolicy.Optional,
    value: MfaPolicy.Optional,
    label: 'Optional',
    raw: MfaPolicy.Optional,
  },
  {
    key: MfaPolicy.Required,
    value: MfaPolicy.Required,
    label: 'Required',
    raw: MfaPolicy.Required,
  },
];

interface FormValues {
  mfaPolicy: MfaPolicy;
}

const schema: z.ZodSchema<FormValues> = z.object({
  mfaPolicy: z.enum(MfaPolicy),
});

export const SecuritySettings = () => {
  const { organization, successToast, errorToast } = useAppContext();

  const defaultValues = {
    mfaPolicy: organization.mfaPolicy,
  };

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const mutation = useUpdateOrganizationMutation();

  const usersQuery = useUsersQuery({
    organizationId: organization.id,
    request: {
      pagination: {
        offset: 0,
        limit: 1000,
      },
    },
  });

  const onSubmit = async (values: FormValues) => {
    const request = P.pickBy(values, (value, key) => {
      return !P.isEqual(value, defaultValues[key]);
    });

    await mutation
      .mutateAsync({
        organizationId: organization.id,
        updateOrganizationRequest: request,
      })
      .then(() => {
        successToast('Security settings updated successfully');
      })
      .catch((err) => {
        errorToast(`Error updating security settings: ${err.message}`);
      });
  };

  const content = P.run(() => {
    if (usersQuery.isLoading) {
      return <Spin />;
    }

    if (usersQuery.error) {
      return <PiccoloError error={usersQuery.error} />;
    }

    const users = usersQuery.data?.organization?.users?.results ?? [];
    const usersWithoutMfa = users.filter((user) => !user.mfaEnrolment);
    const usersWithMfa = users.filter((user) => user.mfaEnrolment);

    const usersWithMfaPercentage =
      users.length > 0 ? Math.round((usersWithMfa.length / users.length) * 100) : 0;
    const usersWithoutMfaPercentage =
      users.length > 0 ? Math.round((usersWithoutMfa.length / users.length) * 100) : 0;

    return (
      <>
        <HookedFormField
          name='mfaPolicy'
          label='Multi-Factor Authentication Policy'
          helperText="Consider requiring MFA for all users to improve your organization's security posture."
          w='md'
        >
          <HookedSegmentGroup name='mfaPolicy' options={mfaPolicyOptions} size='sm' />
        </HookedFormField>

        <ChakraV3.HStack gap={4} align='start' w='lg'>
          <ChakraV3.Stat.Root>
            <ChakraV3.Stat.Label>Users with MFA</ChakraV3.Stat.Label>
            <ChakraV3.HStack gap={4}>
              <ChakraV3.Stat.ValueText>{usersWithMfa.length}</ChakraV3.Stat.ValueText>
              <ChakraV3.Badge colorPalette='green' size='sm'>
                {usersWithMfaPercentage}%
              </ChakraV3.Badge>
            </ChakraV3.HStack>
          </ChakraV3.Stat.Root>

          <ChakraV3.Stat.Root>
            <ChakraV3.Stat.Label>Users without MFA</ChakraV3.Stat.Label>
            <ChakraV3.HStack gap={4}>
              <ChakraV3.Stat.ValueText>{usersWithoutMfa.length}</ChakraV3.Stat.ValueText>
              <ChakraV3.Badge colorPalette='orange' size='sm'>
                {usersWithoutMfaPercentage}%
              </ChakraV3.Badge>
            </ChakraV3.HStack>
          </ChakraV3.Stat.Root>
          <ChakraV3.Stat.Root>
            <ChakraV3.Stat.Label>Total users</ChakraV3.Stat.Label>
            <ChakraV3.Stat.ValueText>{users.length}</ChakraV3.Stat.ValueText>
          </ChakraV3.Stat.Root>
        </ChakraV3.HStack>

        <ChakraV3.Separator />

        <HookedSubmitButton w='fit-content'>Save</HookedSubmitButton>
      </>
    );
  });

  return (
    <FormProvider {...methods}>
      <FormStack gap={6} maxW='lg' as='form' onSubmit={methods.handleSubmit(onSubmit)}>
        <FormSection
          heading='Security'
          description='Configure security settings for your organization'
        >
          <ChakraV3.Separator />
          {content}
        </FormSection>
      </FormStack>
    </FormProvider>
  );
};
